import { type Icon, ChartBarIcon, CalendarBlankIcon, FileTextIcon, HouseIcon, GearIcon, ReceiptIcon, UserIcon, UserCircleIcon, UsersIcon } from "@phosphor-icons/react";


/**
 * A navigáció egyetlen forrása: a fejléc minden fogyasztója (asztali sáv, avatar menü, mobil
 * panel) innen dolgozik – új oldal vagy admin szekció felvételéhez elég itt bővíteni.
 */
export interface NavItem {
  path: string;
  label: string;
  icon: Icon;
  /** Csak pontos egyezésre aktív (a „/” főoldalnak kell, különben mindenhol aktív lenne) */
  end?: boolean;
  /** Számláló a címke mellett (pl. teendők) – 0 vagy undefined esetén nem jelenik meg */
  badge?: number;
}

export interface NavGroup {
  /** Csoportcím a mobil fiókban; nélküle cím nélküli lista */
  title?: string;
  items: NavItem[];
}

/** Amit a fejléc megjelenít – a layout dönti el, mi kerül a sávba és mi a menükbe */
export interface NavModel {
  /** Asztali pill-sor a logó mellett */
  primary: NavItem[];
  /** `iconLabel`: ikon mindig, felirat csak lg-től (alatta tooltip) – hosszabb sorokhoz */
  primaryStyle: "label" | "iconLabel";
  /** Az avatar lenyíló linkjei (a kijelentkezés automatikusan a végére kerül) */
  userMenu: NavItem[];
  /** A mobil fiók csoportjai */
  mobileGroups: NavGroup[];
}

export const ROUTES = {
  home: "/",
  booking: "/booking",
  myBookings: "/my-bookings",
  profile: "/profile",
  login: "/login",
  register: "/register",
  admin: "/admin",
} as const;

const publicNavItems: NavItem[] = [
  { path: ROUTES.home, label: "Főoldal", icon: HouseIcon, end: true },
  { path: ROUTES.booking, label: "Foglalás", icon: CalendarBlankIcon },
];

const myBookingsNavItem: NavItem = { path: ROUTES.myBookings, label: "Foglalásaim", icon: UserIcon };
const profileNavItem: NavItem = { path: ROUTES.profile, label: "Profilom", icon: UserCircleIcon };
const adminNavItem: NavItem = { path: ROUTES.admin, label: "Admin", icon: GearIcon };

export type AdminSectionId =
  | "bookings"
  | "users"
  | "invoices"
  | "analytics"
  | "settings"
  | "changelog";

export interface AdminSection extends NavItem {
  id: AdminSectionId;
}

/** Egy admin szekció címe - a szekciók és a hozzájuk tartozó oldalak (számla-szerkesztő) is ebből épülnek. */
export const adminSectionPath = (id: AdminSectionId): string => `${ROUTES.admin}/${id}`;

const section = (id: AdminSectionId, label: string, icon: Icon): AdminSection => ({
  id,
  path: adminSectionPath(id),
  label,
  icon,
});

export const adminSections: AdminSection[] = [
  section("bookings", "Foglalások", CalendarBlankIcon),
  section("users", "Felhasználók", UsersIcon),
  section("invoices", "Számlák", ReceiptIcon),
  section("analytics", "Analitika", ChartBarIcon),
  section("settings", "Beállítások", GearIcon),
  section("changelog", "Fejlesztések", FileTextIcon),
];

/** Ide irányítunk a csupasz /admin címről */
export const ADMIN_HOME_PATH = adminSections[0].path;

/*
  A számla-szerkesztő saját, teljes képernyős oldal az admin kereten (fejléc, lábléc) KÍVÜL,
  mint az okosmunkalap Kraftja - ezért nem szekció, és nincs menüben: a Számlák listájáról
  és a foglalás részleteiből nyílik. Az útvonal itt él EGY helyen; a hívók az építőket
  használják, a mintát csak az `App.tsx` útvonal-táblája.
*/
export const INVOICE_EDITOR_ROUTE = `${adminSectionPath("invoices")}/new`;
export const INVOICE_DRAFT_EDITOR_ROUTE = `${adminSectionPath("invoices")}/drafts/:draftId`;
/** A foglalás, amiből az új számla indul: `/admin/invoices/new?booking=<id>`. */
export const INVOICE_EDITOR_BOOKING_PARAM = "booking";

/** Új számla - üresen, vagy egy foglalás adataival előkitöltve. */
export function invoiceEditorPath({ bookingId }: { bookingId?: string } = {}): string {
  if (!bookingId) return INVOICE_EDITOR_ROUTE;
  const query = new URLSearchParams({ [INVOICE_EDITOR_BOOKING_PARAM]: bookingId });
  return `${INVOICE_EDITOR_ROUTE}?${query}`;
}

/** Egy mentett piszkozat a szerkesztőben. */
export function invoiceDraftEditorPath(draftId: string): string {
  return INVOICE_DRAFT_EDITOR_ROUTE.replace(":draftId", encodeURIComponent(draftId));
}

/*
  A szerkesztő -> számlalista átadó: sikeres kiállítás után a lista a friss számla részleteit
  nyissa meg.

  Nem `location.state`: a szerkesztő kilépése többnyire VISSZA-lépés (`navigate(-1)`, hogy a
  lista a szűrőivel, a foglalások a nézetükkel együtt kerüljenek elő), egy vissza-lépés pedig
  nem visz új állapotot - a lista a saját, régi bejegyzésének állapotát kapná. Egy modulszintű
  átadó független attól, hogyan érünk a listára. A szerkesztő CSAK akkor tölti ki, ha a
  kilépés a számlalistára visz; a lista a mountkor egyszer kiolvassa és kiüríti. A rövid
  lejárat azt védi, hogy egy el nem fogyasztott átadó percekkel később, egy egészen másik
  látogatáskor nyisson ki egy régi számlát.
*/
export const PENDING_INVOICE_OPEN_TTL_MS = 30_000;

let pendingInvoiceOpen: { invoiceId: string; expiresAt: number } | null = null;

/** A szerkesztő kilépése előtt: a számlalista nyissa meg ezt a számlát, amint megérkezik. */
export function setPendingInvoiceOpen(invoiceId: string, now: number = Date.now()): void {
  pendingInvoiceOpen = { invoiceId, expiresAt: now + PENDING_INVOICE_OPEN_TTL_MS };
}

/** A számlalista mountkor: a megnyitandó számla - egyszer, utána az átadó üres. */
export function consumePendingInvoiceOpen(now: number = Date.now()): string | null {
  const pending = pendingInvoiceOpen;
  pendingInvoiceOpen = null;
  return pending && pending.expiresAt > now ? pending.invoiceId : null;
}

export interface NavViewer {
  signedIn: boolean;
  isAdmin: boolean;
}

/** Publikus oldalak fejléce: főoldal + foglalás, bejelentkezve a saját foglalások és az admin belépő */
export function getSiteNav({ signedIn, isAdmin }: NavViewer): NavModel {
  const primary = [...publicNavItems];
  if (signedIn) primary.push(myBookingsNavItem);
  if (isAdmin) primary.push(adminNavItem);

  const mobileGroups: NavGroup[] = [{ items: publicNavItems }];
  if (signedIn) mobileGroups.push({ title: "Fiókom", items: [myBookingsNavItem, profileNavItem] });
  if (isAdmin) mobileGroups.push({ title: "Adminisztráció", items: [adminNavItem] });

  return {
    primary,
    primaryStyle: "label",
    userMenu: isAdmin ? [profileNavItem, adminNavItem] : [profileNavItem],
    mobileGroups,
  };
}

/** Admin felület: a sávban a szekciók, a publikus linkek az avatar menübe költöznek */
export function getAdminNav(badges: Partial<Record<AdminSectionId, number>> = {}): NavModel {
  const sections = adminSections.map((item) => ({ ...item, badge: badges[item.id] }));
  return {
    primary: sections,
    primaryStyle: "iconLabel",
    userMenu: [...publicNavItems, myBookingsNavItem, profileNavItem],
    mobileGroups: [
      { title: "Adminisztráció", items: sections },
      { title: "Weboldal", items: publicNavItems },
      { title: "Fiókom", items: [myBookingsNavItem, profileNavItem] },
    ],
  };
}
